// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

// Third party packages
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, finalize, skip, startWith, tap } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { InformativeNoteService } from "src/app/modules/companies/informative-note.service";
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { InformativeNoteCompaniesDataSource } from "src/app/shared/utils/data-sources/informative-note-companies-data-source";

/** Key used to remember this list's filters in ListsService */
const LIST_SETTING_NAME = "companies-informative-note";
/** Same limit enforced by the back-end validator */
const SEARCH_MAX_LENGTH = 100;
/** Column filters supported by the back-end (search[<field>]) */
type InformativeNoteFilterField = "name" | "email" | "pec" | "phone";
const FILTER_FIELDS: InformativeNoteFilterField[] = ["name", "email", "pec", "phone"];

/**
 * List of the admin companies with "Nota informativa" enabled in their
 * contractual conditions (condo-owner privacy access key without Privacy SAG)
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-companies-informative-note-list",
  templateUrl: "./companies-informative-note-list.page.html",
  styleUrls: ["./companies-informative-note-list.page.scss"]
})
export class CompaniesInformativeNoteListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUserPermissions: string[] = [];
  displayedColumns: string[] = ["index", "name", "email", "pec", "phone", "actions"];
  dataSource: InformativeNoteCompaniesDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // Column filters (table footer row)
  filters: { [field in InformativeNoteFilterField]: FormControl } = {
    name: new FormControl(""),
    email: new FormControl(""),
    pec: new FormControl(""),
    phone: new FormControl(""),
  };
  searchMaxLength = SEARCH_MAX_LENGTH;
  exportIsLoading = false;

  /**
   * Class constructor
   */
  constructor(
    private informativeNoteService: InformativeNoteService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private configService: ConfigService,
  ) { }

  /**
   * Init component
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.themeService.setPageTitle(
      this.translateService.instant("companies.informativeNoteList.pageTitle")
    );

    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = Array.isArray(res) ? res : [])
      )
    );

    this.dataSource = new InformativeNoteCompaniesDataSource(
      this.informativeNoteService,
      this.helperService
    );

    this.subscribeToListsSettings(LIST_SETTING_NAME);
  }

  /**
   * After view init logic
   *
   * @since 3.0.0
   */
  ngAfterViewInit(): void {
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Reload on sort / page change
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadPage()))
        .subscribe()
    );

    // Server-side column filters: reload (from page 1) once the user stops typing
    this.subscriptions.push(
      combineLatest(
        FILTER_FIELDS.map(field => this.filters[field].valueChanges.pipe(startWith(this.filters[field].value)))
      )
        .pipe(
          skip(1), // initial values are already loaded by the first loadPage() above
          debounceTime(this.configService.debounceTime),
          distinctUntilChanged((previous, current) => previous.join("\u0000") === current.join("\u0000"))
        )
        .subscribe(() => {
          this.paginator.pageIndex = 0;
          this.loadPage();
        })
    );
  }

  /**
   * Handle component destroy
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Remember current list settings
    this.listsService.setListSetting(
      LIST_SETTING_NAME,
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      this.getSearch()
    );
  }

  /**
   * Current column filters, trimmed and bounded to the back-end limit (empty ones omitted)
   */
  private getSearch(): { [key: string]: string } {
    const search: { [key: string]: string } = {};
    FILTER_FIELDS.forEach(field => {
      const raw = this.filters[field].value;
      const value = typeof raw === "string" ? raw.trim().slice(0, SEARCH_MAX_LENGTH) : "";
      if (value) {
        search[field] = value;
      }
    });
    return search;
  }

  /**
   * Load requested data page
   *
   * @since 3.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    this.dataSource.loadItems(
      start,
      length,
      this.sort.active,
      this.sort.direction,
      this.getSearch()
    );
  }

  /**
   * Export the list (current search and sorting) in Excel format
   *
   * @since 3.0.0
   */
  onExport(): void {
    if (this.exportIsLoading) {
      return;
    }
    this.exportIsLoading = true;
    const date = formatDate(new Date(), "yyyyMMddHHmmss", "en-EN");
    const fileName = `${this.translateService.instant("companies.informativeNoteList.exportFileName")} ${date}.xlsx`;
    this.subscriptions.push(
      this.informativeNoteService.getExport(this.sort.active, this.sort.direction, this.getSearch())
        .pipe(finalize(() => (this.exportIsLoading = false)))
        .subscribe(
          res => {
            const blob = new Blob([res], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            FileSaver.saveAs(blob, fileName);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Reset table's filters, sorting and pagination
   *
   * @since 3.0.0
   */
  onResetFilters(): void {
    this.listsService.setListSetting(LIST_SETTING_NAME, 0, 10, {});
    FILTER_FIELDS.forEach(field => this.filters[field].setValue("", { emitEvent: false }));
    this.sort.direction = "asc";
    this.sort.active = "name";
    this.sort._stateChanges.next();
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;
    this.loadPage();
  }

  /**
   * Reset a single column filter (the valueChanges subscription reloads the page)
   *
   * @since 3.0.0
   */
  onResetFilter(field: InformativeNoteFilterField): void {
    this.filters[field].setValue("");
  }

  /**
   * Restore this list's settings (page, page size, search) when the user comes back
   *
   * @since 3.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (!Array.isArray(settings)) {
          return;
        }
        settings.forEach((setting: ListSetting) => {
          if (setting.name !== name) {
            return;
          }
          if (setting.pageIndex) {
            this.paginator.pageIndex = setting.pageIndex;
          }
          if (setting.pageSize) {
            this.paginator.pageSize = setting.pageSize;
          }
          if (setting.search) {
            // No reload here: the first loadPage() in ngAfterViewInit reads these values
            FILTER_FIELDS.forEach(field => {
              if (typeof setting.search[field] === "string") {
                this.filters[field].setValue(setting.search[field], { emitEvent: false });
              }
            });
          }
        });
      })
    );
  }
}
